import { useState } from 'react';
import { T, useGT, Var } from 'gt-react';
import type { TaskTypeViewMatchPlacement } from '@erp/shared-types';
import { useSupplier } from '@/api/rpc/hooks/accounts-payable/useSupplier';
import { useSearchProductVariants } from '@/api/inventory-management/products/useSearchProductVariants';
import { SupplierAutocomplete } from '@/components/company/SupplierAutocomplete';
import { Autocomplete } from '@/components/common/inputs/autocomplete';
import { pickerLabels } from '@/components/common/inputs/pickerLabels';
import { Label } from 'src/components/ui/label';
import { useProductVariantDisplayLabel } from './useProductVariantDisplayLabel';
import {
  productMatchOptions,
  productVariantLabel,
} from './productVariantLabel';
import type { CorrectionCommitResult } from './useCommitTaskCorrection';
import {
  EMPTY_MARK,
  findSchemaField,
  idAtPath,
  placementLabel,
  scalarValuesDiffer,
  setValueAtPath,
  type TaskViewSchemaField,
} from './taskTypeViewValue';

type Commit = (
  next: Record<string, unknown>,
) => void | Promise<CorrectionCommitResult | void>;

/**
 * Supplier name search already lives on the AP supplier autocomplete
 * (`accounts-payable/suppliers?search=`). `accountsPayable.suppliers` stays
 * get-by-id; this reuses that typeahead instead of adding a second query.
 */
export function TaskTypeViewMatch({
  placement,
  schemaFields,
  input,
  workingCopy,
  onCommit,
  hideLabel = false,
  ariaLabel,
}: {
  placement: TaskTypeViewMatchPlacement;
  schemaFields: readonly TaskViewSchemaField[];
  input: Record<string, unknown>;
  workingCopy: Record<string, unknown>;
  onCommit: Commit;
  hideLabel?: boolean;
  ariaLabel?: string;
}) {
  const field = findSchemaField(schemaFields, placement.config.path);
  const label = placementLabel(
    placement.config.label,
    field,
    placement.config.path,
  );
  const inputId = idAtPath(input, placement.config.path);
  const workingId = idAtPath(workingCopy, placement.config.path);
  const name = ariaLabel ?? label;
  const { target, mode } = placement.config;

  async function commitId(nextId: string | null) {
    return onCommit(setValueAtPath(workingCopy, placement.config.path, nextId));
  }

  return (
    <div
      className="flex min-w-0 flex-col gap-1"
      data-testid={`task-view-match-${placement.id}`}
    >
      {hideLabel ? null : <Label>{label}</Label>}
      {mode === 'read' ? (
        <EntityLabel target={target} id={inputId} />
      ) : (
        <EditMatch
          target={target}
          workingId={workingId}
          ariaLabel={name}
          onCommitId={commitId}
        />
      )}
      {mode === 'edit' ? (
        <OriginalMatch
          target={target}
          inputId={inputId}
          workingId={workingId}
        />
      ) : null}
    </div>
  );
}

function OriginalMatch({
  target,
  inputId,
  workingId,
}: {
  target: 'product_variant' | 'supplier';
  inputId: string | null;
  workingId: string | null;
}) {
  const text = useMatchLabel(target, inputId);
  if (!scalarValuesDiffer(inputId, workingId)) return null;
  return (
    <p
      className="text-xs text-muted-foreground"
      data-testid="task-view-original"
    >
      <T context="secondary text under a task view match showing the extracted original entity">
        Original: <Var>{text ?? EMPTY_MARK}</Var>
      </T>
    </p>
  );
}

function EntityLabel({
  target,
  id,
}: {
  target: 'product_variant' | 'supplier';
  id: string | null;
}) {
  const gt = useGT();
  const text = useMatchLabel(target, id);
  if (!text) {
    return (
      <span
        className="text-sm text-muted-foreground"
        aria-label={gt('Empty', {
          context: 'shown when a task view match has no entity',
        })}
      >
        {EMPTY_MARK}
      </span>
    );
  }
  return <span className="text-sm text-stone-800">{text}</span>;
}

function useMatchLabel(
  target: 'product_variant' | 'supplier',
  id: string | null,
): string | null {
  const supplier = useSupplier(
    target === 'supplier' ? (id ?? undefined) : undefined,
  );
  const product = useProductVariantDisplayLabel(
    target === 'product_variant' ? id : null,
  );
  if (!id) return null;
  if (target === 'supplier') return supplier.data?.name ?? id;
  return product;
}

function EditMatch({
  target,
  workingId,
  ariaLabel,
  onCommitId,
}: {
  target: 'product_variant' | 'supplier';
  workingId: string | null;
  ariaLabel: string;
  onCommitId: (nextId: string | null) => Promise<CorrectionCommitResult | void>;
}) {
  const [pending, setPending] = useState<{ id: string | null } | null>(null);
  if (pending && pending.id === workingId) setPending(null);
  const shownId = pending ? pending.id : workingId;

  async function pick(nextId: string | null) {
    if (!scalarValuesDiffer(nextId, workingId)) return;
    setPending({ id: nextId });
    const result = await onCommitId(nextId);
    if (result === 'conflict' || result === 'failed') setPending(null);
  }

  if (target === 'supplier') {
    return (
      <SupplierAutocomplete
        supplierId={shownId ?? undefined}
        onSelect={(next) => {
          void pick(next ? next : null);
        }}
        className="w-full"
      />
    );
  }
  return (
    <ProductVariantMatchControl
      selectedId={shownId}
      ariaLabel={ariaLabel}
      onPick={pick}
    />
  );
}

function ProductVariantMatchControl({
  selectedId,
  ariaLabel,
  onPick,
}: {
  selectedId: string | null;
  ariaLabel: string;
  onPick: (nextId: string | null) => void | Promise<void>;
}) {
  const gt = useGT();
  const [apiQuery, setApiQuery] = useState('');
  const selectedLabel = useProductVariantDisplayLabel(selectedId);
  const searchResults = useSearchProductVariants(
    { query: apiQuery, limit: 20 },
    apiQuery.trim().length > 0,
  );
  const typed = apiQuery.trim().length > 0 ? searchResults.data : [];
  const options = productMatchOptions(typed, selectedId, selectedLabel);

  return (
    <Autocomplete
      deselectable
      allowClear
      items={options}
      value={selectedId}
      onSearch={(query) => setApiQuery(query)}
      debounceMs={150}
      getValue={(option) => option.productVariantId}
      getLabel={(option) =>
        productVariantLabel(option.productName, option.variantSku)
      }
      onValueChange={(value) => {
        void onPick(value === null || value.trim() === '' ? null : value);
      }}
      labels={pickerLabels({
        placeholder: gt('Search by name or SKU', {
          context: 'placeholder for a task view product match',
        }),
        search: gt('Type a name or SKU', {
          context: 'search box placeholder for a task view product match',
        }),
        empty:
          apiQuery.trim().length === 0
            ? gt('Type to search products', {
                context: 'prompt before typing in a task view product match',
              })
            : gt('No products found', {
                context: 'empty results for a task view product match',
              }),
      })}
      renderValue={({ selected }) => (
        <span className="truncate" aria-label={ariaLabel}>
          {productVariantLabel(selected[0].productName, selected[0].variantSku)}
        </span>
      )}
      className="w-full"
    />
  );
}
